import { Link, router, usePage } from "@inertiajs/react"
import { ListChecks } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { caseLogReviewsPath } from "@/routes"
import type { SharedData } from "@/types"

interface CaseSummary {
  case_id: number
  case_name: string
  count: number
}

export function ReviewsDropdown() {
  const { reviews: sharedReviews } = usePage().props as unknown as SharedData
  const unreadCount = sharedReviews.unread_count

  const [open, setOpen] = useState(false)
  const [cases, setCases] = useState<CaseSummary[]>([])
  const [loading, setLoading] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const fetchSummary = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(caseLogReviewsPath({ format: "json" }))
      const data = (await res.json()) as { cases: CaseSummary[] }
      setCases(data.cases)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      void fetchSummary()
    }
  }, [open, fetchSummary])

  const handleCaseClick = (caseId: number) => {
    setOpen(false)
    router.visit(caseLogReviewsPath() + `?case_id=${caseId}`)
  }

  const hasUnread = unreadCount > 0

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative flex cursor-pointer items-center gap-1.5 text-white/80 hover:text-white"
        title="Reviews"
      >
        <ListChecks className="h-5 w-5" />
        {hasUnread && (
          <span className="bg-hpi-active-blue flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="border-hpi-light-gray absolute top-full right-0 z-50 mt-2 w-80 rounded-lg border bg-white shadow-lg">
            {/* Header */}
            <div className="border-hpi-pale-gray flex items-center justify-between border-b px-4 py-3">
              <h3 className="text-hpi-dark-gray text-sm font-semibold">
                Case Updates for Review
              </h3>
              {hasUnread && (
                <span className="bg-hpi-active-blue/15 text-hpi-active-blue rounded-full px-2 py-0.5 text-xs font-medium">
                  {unreadCount}
                </span>
              )}
            </div>

            {/* Case list */}
            <div className="max-h-80 overflow-y-auto">
              {loading && cases.length === 0 && (
                <div className="text-hpi-med-gray px-4 py-8 text-center text-sm">
                  Loading...
                </div>
              )}

              {!loading && cases.length === 0 && (
                <div className="text-hpi-med-gray px-4 py-8 text-center text-sm">
                  All caught up! No updates to review.
                </div>
              )}

              {cases.map((c) => (
                <button
                  key={c.case_id}
                  onClick={() => handleCaseClick(c.case_id)}
                  className="border-hpi-pale-gray hover:bg-hpi-pale-gray flex w-full items-center justify-between border-b px-4 py-2.5 text-left last:border-b-0"
                >
                  <span className="text-hpi-dark-gray text-sm">
                    {c.case_name}
                  </span>
                  <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-gray-500 px-1.5 text-xs font-semibold text-white">
                    {c.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="border-hpi-pale-gray border-t px-4 py-2">
              <Link
                href={caseLogReviewsPath()}
                className="text-hpi-active-blue block text-center text-sm font-medium hover:underline"
                onClick={() => setOpen(false)}
              >
                Review All Case Updates
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
